import { users, auth, setCachedUser, uploadFile, mediaUrl } from '../api.js';
import { navigate } from '../utils/router.js';
import {
  esc,
  avatarUrl,
  toast,
  copyText,
  profileShareUrl,
  badgeHtml,
} from '../utils/dom.js';
import { compressImage } from '../utils/compressImage.js';
import { renderPostCard } from './feed.js';

const AVATAR_MAX = 1 * 1024 * 1024;
const COVER_MAX = 2 * 1024 * 1024;

function coverStyle(user) {
  const url = user?.cover_url ? mediaUrl(user.cover_url) : '';
  if (url) {
    return `background-image:url('${esc(url)}');background-size:cover;background-position:center;`;
  }
  return '';
}

export async function renderProfile(container, gid, { currentUser, onUserUpdate } = {}) {
  container.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
  try {
    const data = await users.get(gid);
    const user = data.user || data;
    const stats = data.stats || {};
    const isSelf = currentUser && String(currentUser.gid) === String(user.gid);
    const isFollowing = !!data.is_following;
    const isMutual = !!data.is_mutual;

    container.innerHTML = `
      <div class="profile-header">
        <div class="profile-cover ${user.cover_url ? 'has-image' : ''}" style="${coverStyle(user)}"></div>
        <div class="profile-row">
          <img class="profile-avatar" src="${esc(avatarUrl(user))}" alt="" />
          <div class="profile-actions">
            ${
              isSelf
                ? `<button type="button" class="btn btn-ghost btn-sm" id="pf-edit">编辑资料</button>`
                : `<button type="button" class="btn ${
                    isFollowing ? 'btn-ghost' : 'btn-primary'
                  } btn-sm" id="pf-follow">${isFollowing ? '已关注' : '关注'}</button>
                   ${
                     isMutual
                       ? `<button type="button" class="btn btn-secondary btn-sm" id="pf-msg">私聊</button>`
                       : ''
                   }`
            }
            <button type="button" class="btn btn-ghost btn-sm" id="pf-share">分享</button>
          </div>
        </div>
        <div class="profile-info">
          <h2>${esc(user.nickname || '用户')}${badgeHtml(user)}</h2>
          <div class="gid">GID ${esc(user.gid)}</div>
          <div class="profile-bio" id="pf-bio">${esc(user.bio || '这个人很懒，还没有简介')}</div>
          <div class="profile-stats">
            <button type="button" id="pf-followers"><strong>${stats.followers ?? 0}</strong><span>粉丝</span></button>
            <button type="button" id="pf-following"><strong>${stats.following ?? 0}</strong><span>关注</span></button>
            <button type="button" disabled><strong>${stats.posts ?? 0}</strong><span>帖子</span></button>
          </div>
        </div>
      </div>
      <div id="pf-list-panel" class="hidden"></div>
      <div id="pf-posts"></div>
    `;

    container.querySelector('#pf-share')?.addEventListener('click', async () => {
      try {
        await copyText(profileShareUrl(user.gid));
        toast('资料链接已复制', 'success');
      } catch {
        toast(profileShareUrl(user.gid), 'info');
      }
    });

    container.querySelector('#pf-follow')?.addEventListener('click', async () => {
      const btn = container.querySelector('#pf-follow');
      btn.disabled = true;
      try {
        if (isFollowing) {
          await users.unfollow(user.gid);
          toast('已取消关注', 'success');
        } else {
          await users.follow(user.gid);
          toast('关注成功', 'success');
        }
        renderProfile(container, gid, { currentUser, onUserUpdate });
      } catch (err) {
        toast(err.message || '操作失败', 'error');
        btn.disabled = false;
      }
    });

    container.querySelector('#pf-msg')?.addEventListener('click', () => {
      navigate(`/messages/${user.gid}`);
    });

    container.querySelector('#pf-edit')?.addEventListener('click', () => {
      openEditProfile(user, async (updated) => {
        setCachedUser(updated);
        onUserUpdate?.(updated);
        renderProfile(container, updated.gid, { currentUser: updated, onUserUpdate });
      });
    });

    const showList = async (type) => {
      const panel = container.querySelector('#pf-list-panel');
      panel.classList.remove('hidden');
      panel.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
      try {
        const res = type === 'followers' ? await users.followers(user.gid) : await users.following(user.gid);
        const items = res.items || res || [];
        panel.innerHTML = `
          <div class="search-section">${type === 'followers' ? '粉丝' : '关注'}列表
            <button type="button" class="btn btn-ghost btn-sm" id="pf-list-close" style="float:right">关闭</button>
          </div>
          ${
            items.length
              ? items
                  .map(
                    (u) => `
            <div class="user-row" data-gid="${esc(u.gid)}">
              <img src="${esc(avatarUrl(u))}" alt="" />
              <div>
                <div class="fw-600">${esc(u.nickname)}${badgeHtml(u)}</div>
                <div class="text-sm muted">GID ${esc(u.gid)}</div>
              </div>
            </div>`
                  )
                  .join('')
              : `<div class="empty-state"><p>暂无</p></div>`
          }
        `;
        panel.querySelector('#pf-list-close')?.addEventListener('click', () => {
          panel.classList.add('hidden');
          panel.innerHTML = '';
        });
        panel.querySelectorAll('[data-gid]').forEach((row) => {
          row.addEventListener('click', () => navigate(`/user/${row.dataset.gid}`));
        });
      } catch (err) {
        panel.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
          err.message
        )}</p></div>`;
      }
    };

    container.querySelector('#pf-followers')?.addEventListener('click', () => showList('followers'));
    container.querySelector('#pf-following')?.addEventListener('click', () => showList('following'));

    const postsEl = container.querySelector('#pf-posts');
    postsEl.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
    try {
      const res = await users.posts(user.gid, { limit: 30 });
      const items = res.items || [];
      postsEl.innerHTML = '';
      const list = document.createElement('div');
      list.className = 'post-list';
      if (!items.length) {
        list.innerHTML = `<div class="empty-state"><p>还没有发布帖子</p></div>`;
      } else {
        items.forEach((p) => {
          list.appendChild(
            renderPostCard(p, {
              currentUser,
              onChange: () => renderProfile(container, gid, { currentUser, onUserUpdate }),
            })
          );
        });
      }
      postsEl.appendChild(list);
    } catch (err) {
      postsEl.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
        err.message
      )}</p></div>`;
    }
  } catch (err) {
    container.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
      err.message || '用户不存在'
    )}</p></div>`;
  }
}

function openEditProfile(user, onSaved) {
  document.getElementById('edit-profile-modal')?.remove();
  const backdrop = document.createElement('div');
  backdrop.id = 'edit-profile-modal';
  backdrop.className = 'modal-backdrop';
  let avatarUrlState = user.avatar_url || null;
  let coverUrlState = user.cover_url || null;

  backdrop.innerHTML = `
    <div class="modal modal-wide">
      <div class="modal-header">
        <h3>编辑资料</h3>
        <button type="button" class="btn btn-ghost btn-sm" data-close>✕</button>
      </div>
      <div class="modal-body">
        <div class="field">
          <label>封面图（资料页顶部区域，≤2MB）</label>
          <div class="ep-cover-preview ${coverUrlState ? 'has-image' : ''}" id="ep-cover-preview"
            style="${coverUrlState ? `background-image:url('${esc(mediaUrl(coverUrlState))}')` : ''}"></div>
          <div class="flex gap-1" style="margin-top:0.5rem;flex-wrap:wrap">
            <label class="btn btn-ghost btn-sm" style="cursor:pointer">
              上传封面
              <input type="file" id="ep-cover-file" accept="image/*" hidden />
            </label>
            <button type="button" class="btn btn-ghost btn-sm" id="ep-cover-clear">恢复默认</button>
          </div>
          <span class="hint">仅支持图片，最大 2MB</span>
        </div>
        <div class="field">
          <label>头像（≤1MB）</label>
          <div class="flex items-center gap-1">
            <img class="ep-avatar-preview" id="ep-avatar-preview" src="${esc(avatarUrl(user))}" alt="" />
            <div>
              <label class="btn btn-ghost btn-sm" style="cursor:pointer">
                上传头像
                <input type="file" id="ep-avatar-file" accept="image/*" hidden />
              </label>
              <div class="hint" style="margin-top:0.35rem">仅支持图片，最大 1MB</div>
            </div>
          </div>
        </div>
        <div class="field">
          <label>昵称</label>
          <input id="ep-nickname" value="${esc(user.nickname || '')}" maxlength="64" />
        </div>
        <div class="field">
          <label>简介</label>
          <textarea id="ep-bio" maxlength="500" rows="4" placeholder="介绍一下自己…">${esc(
            user.bio || ''
          )}</textarea>
          <span class="hint">不可包含敏感词</span>
        </div>
        <div class="field">
          <label>GID</label>
          <input value="${esc(user.gid)}" disabled />
          <span class="hint">GID 不可修改</span>
        </div>
      </div>
      <div class="modal-footer">
        <span class="text-sm muted" id="ep-status"></span>
        <button type="button" class="btn btn-primary btn-sm" id="ep-save">保存</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  backdrop.querySelector('[data-close]')?.addEventListener('click', close);

  const statusEl = backdrop.querySelector('#ep-status');
  const setStatus = (t) => {
    if (statusEl) statusEl.textContent = t || '';
  };

  backdrop.querySelector('#ep-avatar-file')?.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('头像仅支持图片', 'error');
      return;
    }
    try {
      setStatus('压缩头像…');
      const compressed = await compressImage(file, {
        maxBytes: AVATAR_MAX,
        maxWidth: 512,
        maxHeight: 512,
        qualityStart: 0.88,
      });
      if (compressed.size > AVATAR_MAX) {
        toast('头像压缩后仍超过 1MB，请换更小的图片', 'error');
        setStatus('');
        return;
      }
      setStatus('上传头像…');
      const up = await uploadFile(compressed, { type: 'avatar' });
      avatarUrlState = up.url;
      const img = backdrop.querySelector('#ep-avatar-preview');
      if (img) img.src = mediaUrl(up.url);
      setStatus('头像已上传（保存后生效）');
    } catch (err) {
      toast(err.message || '头像上传失败', 'error');
      setStatus('');
    }
  });

  backdrop.querySelector('#ep-cover-file')?.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('封面仅支持图片', 'error');
      return;
    }
    try {
      setStatus('压缩封面…');
      const compressed = await compressImage(file, {
        maxBytes: COVER_MAX,
        maxWidth: 1600,
        maxHeight: 900,
        qualityStart: 0.85,
      });
      if (compressed.size > COVER_MAX) {
        toast('封面压缩后仍超过 2MB，请换更小的图片', 'error');
        setStatus('');
        return;
      }
      setStatus('上传封面…');
      const up = await uploadFile(compressed, { type: 'cover' });
      coverUrlState = up.url;
      const preview = backdrop.querySelector('#ep-cover-preview');
      if (preview) {
        preview.classList.add('has-image');
        preview.style.backgroundImage = `url('${mediaUrl(up.url)}')`;
      }
      setStatus('封面已上传（保存后生效）');
    } catch (err) {
      toast(err.message || '封面上传失败', 'error');
      setStatus('');
    }
  });

  backdrop.querySelector('#ep-cover-clear')?.addEventListener('click', () => {
    coverUrlState = null;
    const preview = backdrop.querySelector('#ep-cover-preview');
    if (preview) {
      preview.classList.remove('has-image');
      preview.style.backgroundImage = '';
    }
    setStatus('已恢复默认封面（保存后生效）');
  });

  backdrop.querySelector('#ep-save')?.addEventListener('click', async () => {
    const nickname = backdrop.querySelector('#ep-nickname').value.trim();
    const bio = backdrop.querySelector('#ep-bio').value;
    if (!nickname) {
      toast('昵称不能为空', 'error');
      return;
    }
    const btn = backdrop.querySelector('#ep-save');
    btn.disabled = true;
    try {
      const res = await auth.updateMe({
        nickname,
        bio,
        avatar_url: avatarUrlState,
        cover_url: coverUrlState,
      });
      const updated = res.user || res;
      toast('资料已更新', 'success');
      close();
      onSaved?.(updated);
    } catch (err) {
      toast(err.message || '保存失败', 'error');
      btn.disabled = false;
    }
  });
}
